import React, { useState, useEffect, useRef } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import Card from '../ui/Card';
import Badge from '../ui/Badge';
import Loading from '../ui/Loading';
import { GripVertical, Save, X } from 'lucide-react';
import type { Camera } from '../../types';
import { cameraService } from '../../services';

interface CameraArrangeModalProps {
  isOpen: boolean;
  onClose: () => void;
  cameras: Camera[];
  locationId: number;
  locationName?: string;
  onSuccess?: () => void;
}

const CameraArrangeModal: React.FC<CameraArrangeModalProps> = ({
  isOpen,
  onClose,
  cameras,
  locationId,
  locationName,
  onSuccess,
}) => {
  const [orderedCameras, setOrderedCameras] = useState<Camera[]>([]);
  const [isSaving, setSaving] = useState(false);
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [autoScrollDirection, setAutoScrollDirection] = useState<'up' | 'down' | null>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollAnimationRef = useRef<number | null>(null);

  // Initialize ordered cameras when modal opens or cameras change
  useEffect(() => {
    if (isOpen && cameras.length > 0) {
      // Filter cameras for this location only, then sort by display_order
      const filtered = cameras.filter(c => c.location_id === locationId);
      const sorted = [...filtered].sort((a, b) => {
        const orderA = a.display_order ?? a.camera_id;
        const orderB = b.display_order ?? b.camera_id;
        return orderA - orderB;
      });
      setOrderedCameras(sorted);
    }
  }, [isOpen, cameras, locationId]);

  // Auto-scroll effect when dragging near edges
  useEffect(() => {
    if (!autoScrollDirection || !scrollContainerRef.current) {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
      return;
    }

    const scroll = () => {
      if (!scrollContainerRef.current || !autoScrollDirection) return;

      const scrollSpeed = 5; // pixels per frame
      const currentScroll = scrollContainerRef.current.scrollTop;
      
      if (autoScrollDirection === 'up') {
        scrollContainerRef.current.scrollTop = Math.max(0, currentScroll - scrollSpeed);
      } else {
        const maxScroll = scrollContainerRef.current.scrollHeight - scrollContainerRef.current.clientHeight;
        scrollContainerRef.current.scrollTop = Math.min(maxScroll, currentScroll + scrollSpeed);
      }

      scrollAnimationRef.current = requestAnimationFrame(scroll);
    };

    scrollAnimationRef.current = requestAnimationFrame(scroll);

    return () => {
      if (scrollAnimationRef.current) {
        cancelAnimationFrame(scrollAnimationRef.current);
        scrollAnimationRef.current = null;
      }
    };
  }, [autoScrollDirection]);

  const handleDragStart = (e: React.DragEvent, index: number) => {
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/html', index.toString());
    setDraggedIndex(index);
  };

  const handleDragEnter = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    const newOrder = [...orderedCameras];
    const draggedItem = newOrder[draggedIndex];
    
    // Remove from old position
    newOrder.splice(draggedIndex, 1);
    // Insert at new position
    newOrder.splice(index, 0, draggedItem);
    
    setOrderedCameras(newOrder);
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    // Auto-scroll detection
    if (scrollContainerRef.current) {
      const container = scrollContainerRef.current;
      const rect = container.getBoundingClientRect();
      const mouseY = e.clientY;
      const edgeThreshold = 80; // pixels from edge to trigger scroll

      // Check if mouse is near top edge
      if (mouseY - rect.top < edgeThreshold && container.scrollTop > 0) {
        setAutoScrollDirection('up');
      }
      // Check if mouse is near bottom edge
      else if (rect.bottom - mouseY < edgeThreshold && 
               container.scrollTop < container.scrollHeight - container.clientHeight) {
        setAutoScrollDirection('down');
      }
      // Not near any edge
      else {
        setAutoScrollDirection(null);
      }
    }
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
    setAutoScrollDirection(null);
  };

  const moveCamera = (index: number, direction: 'up' | 'down') => {
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === orderedCameras.length - 1)
    ) {
      return;
    }

    const newOrder = [...orderedCameras];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;
    [newOrder[index], newOrder[targetIndex]] = [newOrder[targetIndex], newOrder[index]];
    setOrderedCameras(newOrder);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      // Build camera_orders array with 1-based display_order
      const cameraOrders = orderedCameras.map((camera, index) => ({
        camera_id: camera.camera_id,
        display_order: index + 1,
      }));

      await cameraService.updateCameraDisplayOrder(locationId, cameraOrders);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save camera order:', error);
      alert('Failed to save camera order. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Arrange Cameras" size="lg">
      <div className="space-y-4">
        <p className="text-sm text-text-muted">
          {locationName ? `Location: ${locationName} - ` : ''}
          Drag cameras to rearrange their order in the live view grid. 
          The first camera will appear in the top-left position.
        </p>

        <div ref={scrollContainerRef} className="space-y-2 max-h-96 overflow-y-auto">
          {orderedCameras.map((camera, index) => (
            <div
              key={camera.camera_id}
              draggable
              onDragStart={(e) => handleDragStart(e, index)}
              onDragEnter={(e) => handleDragEnter(e, index)}
              onDragOver={handleDragOver}
              onDragEnd={handleDragEnd}
              className={`cursor-move select-none ${
                draggedIndex === index ? 'opacity-50 scale-95' : ''
              }`}
              style={{ userSelect: 'none', WebkitUserSelect: 'none', MozUserSelect: 'none' }}
            >
              <Card
                padding="sm"
                className="hover:shadow-md transition-shadow"
              >
                <div className="flex items-center gap-3">
                  <div className="cursor-grab active:cursor-grabbing">
                    <GripVertical size={20} className="text-text-muted flex-shrink-0" />
                  </div>
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge variant="info" size="sm">
                      #{index + 1}
                    </Badge>
                    <span className="font-medium text-text-primary truncate">
                      {camera.name || camera.camera_location || `Camera ${camera.camera_id}`}
                    </span>
                  </div>
                  <div className="text-xs text-text-muted mt-1">
                    ID: {camera.camera_id} | {camera.camera_location}
                  </div>
                </div>

                <div className="flex gap-1 flex-shrink-0">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveCamera(index, 'up')}
                    disabled={index === 0}
                    title="Move up"
                  >
                    ↑
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => moveCamera(index, 'down')}
                    disabled={index === orderedCameras.length - 1}
                    title="Move down"
                  >
                    ↓
                  </Button>
                </div>
              </div>
              </Card>
            </div>
          ))}
        </div>

        <div className="flex justify-end gap-2 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            <X size={16} />
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving ? (
              <>
                <Loading size="sm" className="mr-2" />
                Saving...
              </>
            ) : (
              <>
                <Save size={16} />
                Save Order
              </>
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default CameraArrangeModal;
